// STARWEAVE — surface scatter (flora + rocks). Dense instanced ground cover so
// worlds feel alive. Everything is placed on the actual terrain surface within
// the play patch, oriented to the surface normal, and varies by archetype.
// One InstancedMesh per type => a few draw calls for thousands of objects.

import * as THREE from 'three';
import type { Planet } from './planet';

function lcg(seed: number) {
  let s = seed >>> 0;
  return () => { s = (Math.imul(s, 1664525) + 1013904223) >>> 0; return s / 4294967296; };
}

interface ScatterType {
  geo: THREE.BufferGeometry;
  count: number;
  scale: [number, number];     // min,max
  colors: number[];            // palette (per-instance)
  emissive?: number;           // glow tint for alien flora
}

function bladeGeo(): THREE.BufferGeometry {
  const g = new THREE.ConeGeometry(0.16, 1.6, 4, 1);
  g.translate(0, 0.8, 0);
  return g;
}
function frondGeo(): THREE.BufferGeometry {
  // a tall tapered spire — alien grass / reed
  const g = new THREE.CylinderGeometry(0.03, 0.22, 3.2, 5, 1);
  g.translate(0, 1.6, 0);
  return g;
}
function bushGeo(): THREE.BufferGeometry {
  const g = new THREE.IcosahedronGeometry(1, 1);
  g.scale(1, 1.25, 1); g.translate(0, 0.9, 0);
  return g;
}
function rockGeo(): THREE.BufferGeometry {
  const g = new THREE.DodecahedronGeometry(1, 0);
  g.translate(0, 0.4, 0);
  return g;
}

const UP = new THREE.Vector3(0, 1, 0);

/** Scatter ground cover within the patch around `anchorDir`. `minHeight` keeps
 *  flora above sea level (0..1 terrain height). Returns the added meshes. */
export function scatterSurface(
  scene: THREE.Scene,
  planet: Planet,
  anchorDir: THREE.Vector3,
  archetype: number,
  seed: number,
  minHeight: number,
): THREE.InstancedMesh[] {
  const tangent = new THREE.Vector3().crossVectors(anchorDir, UP).normalize();
  const bitan = new THREE.Vector3().crossVectors(anchorDir, tangent).normalize();
  const rng = lcg(seed ^ 0x5f3a91);
  const PATCH = 0.34; // angular radius of the scatter patch (spread, not a clump)

  // Per-archetype scatter recipe.
  const lush = archetype === 0 || archetype === 4;
  const desert = archetype === 3;
  const volcanic = archetype === 6;
  const frozen = archetype === 5;

  const types: ScatterType[] = [];
  if (lush) {
    types.push({ geo: bladeGeo(), count: 4200, scale: [1.4, 3.2], colors: [0x4a9a3a, 0x6fb84a, 0x3c7a3a, 0x86c24f] });
    types.push({ geo: frondGeo(), count: 600, scale: [1.6, 3.0], colors: [0x2f8f6a, 0x39b58a, 0x7ad0a0] });
    types.push({ geo: bushGeo(), count: 320, scale: [1.0, 2.4], colors: [0x2b7a5a, 0x9b5fd0, 0xe06aa0], emissive: 0x301840 });
    types.push({ geo: rockGeo(), count: 180, scale: [0.8, 2.6], colors: [0x6b6256, 0x57606a] });
  } else if (desert) {
    types.push({ geo: frondGeo(), count: 220, scale: [1.4, 3.0], colors: [0x8a7a3a, 0xb59a4a] });
    types.push({ geo: rockGeo(), count: 420, scale: [0.8, 3.4], colors: [0x9a7a4a, 0xb89a64, 0x7a5a3a] });
  } else if (volcanic) {
    types.push({ geo: rockGeo(), count: 520, scale: [0.9, 3.6], colors: [0x2a2622, 0x3a3026, 0x554036] });
    types.push({ geo: bushGeo(), count: 120, scale: [0.6, 1.6], colors: [0xff5a1a, 0xffaa33], emissive: 0xff4400 });
  } else if (frozen) {
    types.push({ geo: rockGeo(), count: 360, scale: [0.8, 2.8], colors: [0xbfcad6, 0x9fb0c0, 0xe6eef5] });
    types.push({ geo: bushGeo(), count: 140, scale: [0.7, 1.8], colors: [0x6fb6ff, 0xbfe0ff], emissive: 0x183048 });
  } else {
    types.push({ geo: rockGeo(), count: 360, scale: [0.8, 3.0], colors: [0x6b6256, 0x57606a, 0x7a6a5a] });
  }

  const meshes: THREE.InstancedMesh[] = [];
  const tmpDir = new THREE.Vector3();
  const tmpPos = new THREE.Vector3();
  const m = new THREE.Matrix4();
  const q = new THREE.Quaternion();
  const spin = new THREE.Quaternion();
  const sv = new THREE.Vector3();
  const col = new THREE.Color();

  for (const t of types) {
    const mat = new THREE.MeshStandardMaterial({
      vertexColors: false, roughness: 0.85, metalness: 0.02, flatShading: true,
      emissive: t.emissive ?? 0x000000, emissiveIntensity: t.emissive ? 0.5 : 0,
    });
    const inst = new THREE.InstancedMesh(t.geo, mat, t.count);
    let placed = 0;
    let tries = 0;
    while (placed < t.count && tries < t.count * 4) {
      tries++;
      // uniform-ish disk in the patch
      const a = rng() * Math.PI * 2;
      const r = Math.sqrt(rng()) * PATCH;
      tmpDir.copy(anchorDir).addScaledVector(tangent, Math.cos(a) * r).addScaledVector(bitan, Math.sin(a) * r).normalize();
      if (planet.heightUnit(tmpDir) < minHeight) continue; // underwater
      tmpPos.copy(planet.surfacePoint(tmpDir));
      q.setFromUnitVectors(UP, tmpDir);
      spin.setFromAxisAngle(tmpDir, rng() * Math.PI * 2);
      q.premultiply(spin);
      const s = t.scale[0] + rng() * (t.scale[1] - t.scale[0]);
      sv.set(s, s * (0.85 + rng() * 0.4), s);
      m.compose(tmpPos, q, sv);
      inst.setMatrixAt(placed, m);
      col.setHex(t.colors[(rng() * t.colors.length) | 0]).multiplyScalar(0.8 + rng() * 0.4);
      inst.setColorAt(placed, col);
      placed++;
    }
    inst.count = placed;
    inst.instanceMatrix.needsUpdate = true;
    if (inst.instanceColor) inst.instanceColor.needsUpdate = true;
    scene.add(inst);
    meshes.push(inst);
  }
  return meshes;
}
